import { navigateMediaItem } from "@/helpers/navigateMediaItem.js";
import { headerLine } from '@/modules/parts/headerLine';
import { ctaButton } from "@/modules/parts/ctaButton";
import { mobileMenuArea } from "@/modules/parts/mobile/mobileMenuArea";
import { closeSwipeAction } from '@/modules/actions/mobile/closeSwipeAction';
import { volumeButtonClickAction } from '@/modules/actions/volumeButtonClickAction';
import { closeButtonClickAction } from '@/modules/actions/closeButtonClickAction';
import { sharingClickAction } from '@/modules/actions/sharingClickAction';
import { desktopMenuArea } from "@/modules/parts/desktop/desktopMenuArea";
import { mobileVideoControls, activeVideoPlay } from "@/modules/actions/videoControls";
import { desktopArrowIcons } from "@/modules/parts/desktop/desktopArrowIcons";
import { preloadVideo } from "@/helpers/preloadVideo";
import { playEventHandler } from "@/helpers/playEventHandler";
import { trackMediaProcess } from "@/helpers/trackMediaProcess";
import { createSlide } from '@/modules/createSlide';
import initSwiper from "@/plugins/initSwiper";
import { getSwiperInstance } from "@/helpers/swiperInstance";
import { desktopArrowIconsAction } from '@/modules/actions/desktop/desktopArrowIconsAction';
import { groupMoveButton } from '@/modules/parts/desktop/groupMoveButton';
import { applySlideScaling } from "@/modules/actions/slideScaleAction";
import { updateHeaderWidthToSlide } from "@/modules/actions/updateHeaderWidthAction";
import { groupMoveButtonAction } from '@/modules/actions/desktop/groupMoveButtonAction';
import { clickCtaAction } from '@/modules/actions/clickCtaAction';
import { setupAutoPlayNextAction } from '@/modules/actions/desktop/setupAutoPlayNextAction';
import { injectHeaderLineStyles, animateCurrentProgressBar, animateCurrentProgressBarInElement, prepareCurrentProgressBar, prepareCurrentProgressBarInElement, pauseAllProgressBars, resumeAllProgressBars } from '@/modules/actions/headerLineDuration.js';
import { injectMediaShimmerStyles, showMediaLoadingShimmer, hideMediaLoadingShimmer } from '@/helpers/mediaLoadingShimmer';
import { slideClickNavigateAction } from "@/modules/actions/desktop/slideClickNavigateAction";
import { changeMovieTapAction } from "@/modules/actions/mobile/changeMovieTapAction";
import { groupSwipeAction } from "@/modules/actions/mobile/groupSwipeAction";
import { swipeCtaAction } from "@/modules/actions/mobile/swipeCtaAction";
import { mobileAutoPlayNextAction } from "@/modules/actions/mobile/mobileAutoPlayNextAction";
import { updateCtaButtonAction } from "@/modules/actions/mobile/updateCtaButtonAction";
import { visibilityChangeAction } from "@/modules/actions/visibilityChangeAction";
import { videoBufferingAction } from "@/modules/actions/videoBufferingAction";
import { preventFullscreen } from '@/modules/actions/preventFullscreen';
import { introSquareSlideAnimation } from '@/modules/actions/mobile/introSquareSlideAnimation';
import { circleOverlayAction } from '@/modules/actions/mobile/circleOverlayAction';
import { clearImageTimer, preloadNextStoryVideo, commonChangeMediaProcess, isMediaImageReady } from '@/helpers/mediaUtils';
import { checkVolumeButtonState } from '@/helpers/volumeControl';
import { trackStoreeelEvent } from '@/helpers/trackStoreeelEvent';
import { buildMediaAssetViewParams } from '@/helpers/buildMediaAssetViewParams';
import { buildMediaAssetIdParam } from '@/helpers/buildMediaAssetIdParam';
import { isTrackableMediaId } from '@/helpers/isTrackableMediaId';
import localStorageRepositories from '@/plugins/localStorageRepositories';
import { SHARE_OPEN_TIMEOUT_MS } from '@/helpers/openSharedContent';
import { isCampaignLoopPlaybackEnabled } from '@/helpers/campaignSettings';
import { applyFrequencyInsertContents, initFreqInsertCircleState, injectFreqInsertIntoGroup, recordDisplayedContent } from '@/helpers/streamsTargeting';
import { normalizeSquareContentIndex } from '@/helpers/squareContentIndex';
import { updateAllCtaHostPositions, updateCtaHostPositionInWrapper } from '@/helpers/ctaHostPosition';
import { maybeShowTargetingQuestionnaireForCurrentContent } from '@/helpers/maybeShowTargetingQuestionnaire';
import { applyQuestionnaireResumeOpen } from '@/helpers/applyQuestionnaireResumeOpen';
import { closeModal } from '@/helpers/closeModal';
import {
  createStoreeelModalHost,
  createStoreeelModalShadowRootContainer,
} from '@/helpers/storeeelShadowDom';
import { isReelLike } from '@/helpers/isReelLike';

const applyModalRootStyles = (modal, overrides = {}) => {
  Object.entries({
    display: 'block',
    position: 'fixed',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    margin: '0',
    padding: '0',
    boxSizing: 'border-box',
    overflow: 'hidden',
    zIndex: '9999999999',
    backgroundColor: 'rgba(39, 39, 39, 0.87)',
    backdropFilter: 'blur(6px)',
    visibility: 'visible',
    filter: 'none',
    clipPath: 'none',
    pointerEvents: 'auto',
    isolation: 'isolate',
    ...overrides,
  }).forEach(([property, value]) => {
    if (value == null || value === '') return;
    modal.style[property] = value;
  });
};

export const setupVideoPlayerModal = () => {
  window._storeeelPcSquareLoopRepeat = false;
  window._storeeelPcSquareLoopOffset = 0;
  const questionnaireResumePosition = window.__storeeelQuestionnaireResumePosition || null;
  // リール型・タイル型はアイコンDOMのid形式でも再オープンされるため、初期スライド計算前に正規化する。
  // フィードフォルダの仮想グループIDは `feed-folder-group-{contentId}-{feedProductId}-{index}` と
  // ハイフンを含むため split('-').pop() では壊れる（末尾の連番しか取れず findIndex が常に -1 になり、
  // どのタイルをタップしても1件目が開く）。campaignIconsTapAction.js の getSelectedGroupId と同じ
  // プレフィックス除去方式に揃える。
  if (window.storeeelItems.isMobile && isReelLike(window.storeeelItems.frame)) {
    window.iconId = String(questionnaireResumePosition?.iconId || window.iconId || '').replace(/^storeeel-icon-/, '');
    if (Number.isFinite(questionnaireResumePosition?.moviePosition)) {
      window.moviePosition = questionnaireResumePosition.moviePosition;
    }
  }
  const modalHost = createStoreeelModalHost();
  const { shadowRoot, container: modalShadowContainer } = createStoreeelModalShadowRootContainer(modalHost);
  document.body.appendChild(modalHost);

  // PC表示時のみ、前後スライドにグレーのオーバーレイを追加
  if (!window.storeeelItems.isMobile) {
    const overlayStyle = document.createElement('style');
    overlayStyle.textContent = `
      .storeeel-swiper-group-wrapper .swiper-slide-prev::after,
      .storeeel-swiper-group-wrapper .swiper-slide-next::after {
        content: '';
        position: absolute;
        top: 0; left: 0;
        width: 100%; height: 100%;
        background-color: rgba(0, 0, 0, 0.5);
        pointer-events: none;
        border-radius: 30px;
        z-index:10000;
      }
    `;
    shadowRoot.appendChild(overlayStyle);
  }

  // 不要な余白を削除
  // SP時のみ storeeelSwiper 内の swiper-wrapper に margin:0!important を適用
  if (window.storeeelItems.isMobile) {
    const mobileStyle = document.createElement('style');
    mobileStyle.textContent = `
      .storeeelSwiper .swiper-wrapper {
        margin: 0 !important;
      }
    `;
    shadowRoot.appendChild(mobileStyle);
  }
  // 全画面にモーダルを表示
  const modal = document.createElement('div');
  modal.className = 'storeeel-modal';
  modal.dataset.storeeelPreviousBodyOverflow = document.body.style.overflow || '';
  modal.dataset.storeeelModalShadow = 'true';
  // モーダル表示はスライドインではなくフェードインで見せる
  applyModalRootStyles(modal, {
    opacity: '0',
    transition: 'opacity 0.5s ease',
  });
  document.body.style.overflow = 'hidden';

  modalShadowContainer.appendChild(modal);
  if (!document.__storeeelModalEscapeBound) {
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      if (!document.querySelector('.storeeel-modal')) return;
      closeModal();
    });
    document.__storeeelModalEscapeBound = true;
  }
  if (!window.storeeelItems.isMobile) {
    // PCでは中央（active）以外の動画再生を常に抑止する
    const blockNonActiveVideoPlayback = (event) => {
      const target = event?.target;
      if (!(target instanceof HTMLVideoElement)) return;
      if (!modal.contains(target)) return;
      const activeSlide = modal.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active');
      if (activeSlide && activeSlide.contains(target)) return;
      target.autoplay = false;
      target.removeAttribute('autoplay');
      try { target.pause(); } catch { void 0; }
      try { target.currentTime = 0; } catch { void 0; }
    };
    modal.addEventListener('play', blockNonActiveVideoPlayback, true);
    modal.addEventListener('playing', blockNonActiveVideoPlayback, true);
  }
  // iPhone のときのみ、モーダル内の全動画で全画面抑制を再バインド
  if (window.isIphone) {
    modal.querySelectorAll('video').forEach(videoEl => preventFullscreen(videoEl));
  }

  const stopVideosForShareOverlay = () => {
    modal.querySelectorAll('video').forEach((videoEl) => {
      videoEl.dataset.storeeelAutoplayBlocked = '1';
      videoEl.autoplay = false;
      videoEl.removeAttribute('autoplay');
      try { videoEl.pause(); } catch { void 0; }
    });
  };

  const resumeVideosAfterShareOverlay = () => {
    modal.querySelectorAll('video[data-storeeel-autoplay-blocked="1"]').forEach((videoEl) => {
      delete videoEl.dataset.storeeelAutoplayBlocked;
      videoEl.autoplay = true;
      videoEl.setAttribute('autoplay', '');
    });
    // アクティブスライドのみ再生を再開
    activeVideoPlay();
  };

  const shareState = window.__storeeelShareOpen;
  const useShareLoadingOverlay = Boolean(shareState && !shareState.used);
  if (useShareLoadingOverlay) {
    modal.classList.add('storeeel-share-overlay-active');
    applyModalRootStyles(modal, {
      opacity: '1',
      visibility: 'visible',
      transform: 'none',
      transition: 'opacity 0.5s ease',
    });
    shareState.modalHidden = true;
    shareState.used = true;
    stopVideosForShareOverlay();
    pauseAllProgressBars();

    if (!document.getElementById('storeeel-share-loading-style')) {
      const loadingStyle = document.createElement('style');
      loadingStyle.id = 'storeeel-share-loading-style';
      loadingStyle.textContent = `
        .storeeel-share-loading {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(39, 39, 39, 0.9);
          z-index: 1;
        }
        .storeeel-share-loading__spinner {
          width: 36px;
          height: 36px;
          border: 3px solid rgba(255, 255, 255, 0.35);
          border-top-color: rgba(255, 255, 255, 0.9);
          border-radius: 50%;
          animation: storeeel-share-spin 0.8s linear infinite;
        }
        @keyframes storeeel-share-spin {
          to { transform: rotate(360deg); }
        }

        .storeeel-share-overlay-active .storeeel-movie-select_banner::before {
          animation-play-state: paused !important;
        }
      `;
      shadowRoot.appendChild(loadingStyle);
    }

    const overlay = document.createElement('div');
    overlay.className = 'storeeel-share-loading';
    overlay.innerHTML = '<div class="storeeel-share-loading__spinner"></div>';
    modal.appendChild(overlay);
    shareState.loadingOverlay = overlay;

    shareState.restoreModal = () => {
      const wrapper = modal.querySelector('.storeeel-swiper-group-wrapper');
      if (wrapper) {
        wrapper.style.visibility = 'visible';
      }
      if (shareState.loadingOverlay?.parentNode) {
        shareState.loadingOverlay.parentNode.removeChild(shareState.loadingOverlay);
      }
      applyModalRootStyles(modal, {
        visibility: 'visible',
        opacity: '1',
        transition: 'opacity 0.5s ease',
      });
      modal.classList.remove('storeeel-share-overlay-active');
      resumeAllProgressBars();
      resumeVideosAfterShareOverlay();
    };
    shareState.timeoutId = window.setTimeout(() => {
      shareState.restoreModal?.();
    }, SHARE_OPEN_TIMEOUT_MS);
  } else {
    setTimeout(() => {
      applyModalRootStyles(modal, {
        opacity: '1',
        transform: 'none',
        transition: 'opacity 0.5s ease',
      });
    }, 10);
  }

  // PCかつ、リール型・タイル型以外はグループ内のコンテンツを取得
  let contents = (!window.storeeelItems.isMobile && isReelLike(window.storeeelItems.frame))
    ? []
    : navigateMediaItem();

  // swiperのスライドを生成
  function setupSwiperGroup() {
    const groupWrapper = document.createElement('div');
    groupWrapper.className = 'storeeel-swiper-group-wrapper';

    const swiperContainer = document.createElement('div');
    swiperContainer.className = 'storeeel-swiper-container';

    const swiperElement = document.createElement('div');
    swiperElement.className = 'swiper storeeelSwiper';
    swiperElement.style.inset = '0';
    swiperElement.style.margin = '0';
    swiperElement.style.padding = '0';

    // 外部テーマからの.swiper干渉を防ぐ最低限リセット（レイアウト系のみ）
    const isolationStyle = document.createElement('style');
    let isolationCss = `
      .storeeel-swiper-group-wrapper .swiper.storeeelSwiper {
        margin: 0 !important;
        padding: 0 !important;
        inset: 0 !important;
        box-sizing: border-box !important;
        position: relative !important;
        display: flex !important;
        width: 100% !important;
        height: 100% !important;
        overflow: hidden !important;
      }

      .storeeel-swiper-group-wrapper .swiper.storeeelSwiper .swiper-wrapper {
        display: flex !important;
        transition-property: transform !important;
        box-sizing: content-box !important;
      }

      .storeeel-swiper-group-wrapper .swiper.storeeelSwiper .swiper-slide {
        flex-shrink: 0 !important;
        position: relative !important;
        transition-property: transform !important;
      }

      /* 外部テーマの .swiper-slide div padding を打ち消す */
      .storeeel-swiper-group-wrapper .swiper-slide .slide-media-wrapper {
        padding: 0 !important;
        margin: 0 !important;
      }
    `;

    if (window.storeeelItems.isMobile && window.storeeelItems.frame === 'circle') {
      isolationCss += `
        .storeeel-swiper-group-wrapper .swiper-slide .slide-media-wrapper {
          visibility: hidden !important;
        }

        .storeeel-swiper-group-wrapper .swiper-slide.swiper-slide-active .slide-media-wrapper {
          visibility: visible !important;
        }

        /* ゆっくりスワイプ中でも cube の隣面が真っ黒にならないよう、スワイプ中だけ前後スライドも表示する */
        .storeeel-swiper-group-wrapper.storeeel--swiping .swiper-slide.swiper-slide-next .slide-media-wrapper,
        .storeeel-swiper-group-wrapper.storeeel--swiping .swiper-slide.swiper-slide-prev .slide-media-wrapper {
          visibility: visible !important;
        }
      `;
    }

    isolationStyle.textContent = isolationCss;
    shadowRoot.appendChild(isolationStyle);

    swiperContainer.appendChild(swiperElement);

    const wrapper = document.createElement('div');
    wrapper.className = 'swiper-wrapper';
    // モバイルかつ circle ではない場合のみ、wrapper の高さを 100dvh に固定
    if (window.storeeelItems.isMobile && window.storeeelItems.frame !== 'circle') {
      wrapper.style.height = '100dvh';
    }

    // Swiper(loop) + slidesPerView=2.6 では最低6枚ないとループが不安定になるため増やす
    const PC_SQUARE_LOOP_MIN_SLIDES = 6;
    const repeatSquareContentsForLoop = (source) => {
      if (!Array.isArray(source) || source.length === 0) return [];
      if (source.length >= PC_SQUARE_LOOP_MIN_SLIDES) return source;
      // ループ時に隣スライドを常に表示するため、順序を保ったまま複製する
      const repeatCount = Math.ceil(PC_SQUARE_LOOP_MIN_SLIDES / source.length);
      const repeated = [];
      for (let i = 0; i < repeatCount; i += 1) {
        repeated.push(...source);
      }
      return repeated;
    };

    // square/tile: グループ間に頻度挿入コンテンツをグループ単位で挿入
    if (isReelLike(window.storeeelItems.frame)) {
      const groups = window.storeeelItems.contentGroups || [];
      let startGroupIndex;
      if (!window.storeeelItems.isMobile) {
        startGroupIndex = window.moviePosition || 0;
      } else {
        const groupIdx = Math.max(0, groups.findIndex(({ id }) => id == window.iconId));
        startGroupIndex = groups.slice(0, groupIdx).reduce((sum, g) => sum + (g.contents?.length || 0), 0);
      }
      window.storeeelItems = {
        ...window.storeeelItems,
        contentGroups: applyFrequencyInsertContents(window.storeeelItems, groups, startGroupIndex),
      };
    }

    // circle: 開いたグループの contents 内に頻度挿入コンテンツを注入（グループ選択時点で進捗バーに反映）
    if (window.storeeelItems.frame === 'circle') {
      const freqState = initFreqInsertCircleState(window.storeeelItems);
      window._storeeelFreqInsertCircleState = freqState;
      if (freqState) {
        const groups = window.storeeelItems.contentGroups || [];
        const groupIdx = groups.findIndex((g) => String(g.id) === String(window.iconId));
        if (groupIdx >= 0) {
          const injectedGroup = injectFreqInsertIntoGroup(groups[groupIdx], freqState);
          injectedGroup._freqInsertInjected = true;
          groups[groupIdx] = injectedGroup;
          window.storeeelItems = { ...window.storeeelItems, contentGroups: groups };
          // PC + circle: contents を更新してヘッダーバー数を正しくする
          if (!window.storeeelItems.isMobile) {
            contents = injectedGroup.contents;
          }
          // スマホ円形: モーダルを開いた最初のコンテンツは初期表示経路で描画され
          // commonChangeMediaProcess を通らずカウントされない。ここで1回だけ記録して
          // contentsSeen のズレ（次グループ先頭への pre-insert 漏れ）を防ぐ。
          // recordDisplayedContent は seenNormalContentIds の Set で重複排除するため二重カウントしない。
          if (window.storeeelItems.isMobile) {
            const initialContent = injectedGroup.contents[Number(window.moviePosition) || 0];
            if (initialContent) recordDisplayedContent(initialContent, freqState);
          }
        }
      }
    }

    // デバイスと表示方法によってswiperのグループ化の処理を変える
    if (window.storeeelItems.isMobile) {
      // モバイル
      if (window.storeeelItems.frame === 'circle') {
        // circle時のみグループごとの最初の動画を使う
        const groups = window.storeeelItems.contentGroups || [];
        // 開いたグループ以外の動画は src を保留する（全グループ同時読み込みで帯域を奪い合わないように）
        groups.forEach(group => {
          const firstContent = group.contents?.[0];
          if (firstContent) {
            const deferSrc = String(group.id) !== String(window.iconId);
            wrapper.appendChild(createSlide(firstContent, { deferSrc }));
          }
        });
      } else {
        // SP + square: 全てのグループの最初の動画を順序どおりに追加
        const groups = window.storeeelItems.contentGroups || [];
        const loopPlaybackEnabled = isCampaignLoopPlaybackEnabled(window.storeeelItems);
        const firstContents = groups
          .map(group => group.contents?.[0])
          .filter(Boolean);
        const shouldDuplicateSingleContent = loopPlaybackEnabled && firstContents.length === 1;

        if (shouldDuplicateSingleContent) {
          // ループ有効かつ1件のみの場合、スワイプできるようにスライドを複製する
          firstContents.push(firstContents[0]);
        }

        // 開いたスライド以外は src を保留し、表示中 → 前後の順に読み込む（reelMediaLoadWindow）
        const openedIndex = Math.max(0, groups.findIndex(({ id }) => id == window.iconId));
        firstContents.forEach((content, index) => {
          wrapper.appendChild(createSlide(content, { deferSrc: index !== openedIndex }));
        });
      }
    } else {
      // PC
      // circleの場合は、選択したグループ内のコンテンツを全てswiperに追加
      // 開いたスライド以外は src を保留し、中央 → 隣の順に読み込む（pcMediaLoadWindow）
      if (window.storeeelItems.frame === 'circle') {
        const openedIndex = Number(window.moviePosition || 0);
        contents.forEach((content, index) => {
          wrapper.appendChild(createSlide(content, { deferSrc: index !== openedIndex }));
        });
      } else {
        // squareの場合は、全てのコンテンツをswiperに追加
        const allContents = window.storeeelItems.contentGroups.flatMap(group => group.contents);
        const loopPlaybackEnabled = isCampaignLoopPlaybackEnabled(window.storeeelItems);
        const displayContents = loopPlaybackEnabled
          ? repeatSquareContentsForLoop(allContents)
          : allContents;

        if (loopPlaybackEnabled && displayContents.length > allContents.length) {
          // PC + リール型の少数コンテンツ時は複製スライドを使う
          window._storeeelPcSquareLoopRepeat = true;
          if (allContents.length > 0) {
            const repeatCount = Math.floor(displayContents.length / allContents.length);
            // ループの折り返しが目立たないよう、中央寄りのサイクルを初期表示にする
            window._storeeelPcSquareLoopOffset = Math.floor(repeatCount / 2) * allContents.length;
          }
        }
        const openedIndex = Number(window.moviePosition || 0) + (window._storeeelPcSquareLoopOffset || 0);
        displayContents.forEach((content, index) => {
          wrapper.appendChild(createSlide(content, { deferSrc: index !== openedIndex }));
        });
      }
    }

    swiperElement.appendChild(wrapper);
    groupWrapper.appendChild(swiperContainer);

    return groupWrapper;
  }

  // Swiper の DOM を生成
  const groupWrapperNode = setupSwiperGroup(contents);
  modal.appendChild(groupWrapperNode);
  if (shareState?.modalHidden && useShareLoadingOverlay) {
    groupWrapperNode.style.visibility = 'hidden';
  }
  // PC 時のみ矢印アイコンを swiper-wrapper 内に挿入
  if (!window.storeeelItems.isMobile) {
    const wrapper = modal.querySelector('.storeeel-swiper-group-wrapper');
    if (wrapper) {
      wrapper.insertAdjacentHTML('beforeend', desktopArrowIcons());
    }
  }
  // 2025-05-03: PC時のみ swiper-group-wrapper を中央に固定配置
  if (!window.storeeelItems.isMobile) {
    Object.entries({
      position: 'fixed',
      top: '54%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      'max-width': '1150px',
      width: '63%',
      'min-width': '700px',
      'z-index': '100',
    }).forEach(([property, value]) => {
      groupWrapperNode.style.setProperty(property, value);
    });
  }

  // 動画進捗バーの生成
  function setupProgressBar() {
    if (window.storeeelItems.isMobile) {
      // モバイル
      const allSlides = document.querySelectorAll('.storeeel-swiper-group-wrapper .swiper-slide');

      if (window.storeeelItems.frame === "circle") {
        const groups = window.storeeelItems.contentGroups || [];

        allSlides.forEach((slide, index) => {
          const group = groups[index];
          const count = group?.contents?.length || 0;
          const groupName = group?.name || '';
          const headerElement = headerLine(count, groupName);
          slide.prepend(headerElement);
        });
      } else {
        // 通常SP（circleでない）
        allSlides.forEach((slide, index) => {
          const groups = window.storeeelItems.contentGroups || [];
          // ループ時の複製スライドでもグループ名を維持するため循環参照する
          const groupIndex = groups.length > 0 ? (index % groups.length) : index;
          const groupName = groups[groupIndex]?.name || '';
          // リール型（square）は「1グループ=1コンテンツ」想定のため、進捗バーは常に1本
          const headerElement = headerLine(1, groupName);
          slide.prepend(headerElement);
        });
      }
    } else {
      // PC
      if (window.storeeelItems.frame === 'circle') {
        const storeeelItems = window.storeeelItems;
        const tapGroupId = window.iconId
        window.iconId = tapGroupId.split("-").pop()
        const currentGroupIndex = storeeelItems.contentGroups.findIndex(group => group.id === window.iconId);
        let currentGroup = storeeelItems.contentGroups[currentGroupIndex];
        const swiperGroupWrapper = modal.querySelector('.storeeel-swiper-group-wrapper');
        if (swiperGroupWrapper) {
          const headerElement = headerLine(contents.length, currentGroup.name);
          swiperGroupWrapper.appendChild(headerElement);
        }
      } else {
        const swiperGroupWrapper = modal.querySelector('.storeeel-swiper-group-wrapper');
        if (swiperGroupWrapper) {
          const headerElement = headerLine(1);
          swiperGroupWrapper.appendChild(headerElement);
        }
      }
    }

    if (window.storeeelItems.isMobile) {
      injectHeaderLineStyles();
    }
  }
  setupProgressBar();
  injectMediaShimmerStyles();

  // メニューエリアの生成
  function setupMenuArea() {
    if (window.storeeelItems.isMobile) {
      modal.insertAdjacentHTML('beforeend', mobileMenuArea());
    } else {
      if (window.storeeelItems.frame === "circle") {
        // 左右下のグループ移動ボタン
        modal.insertAdjacentHTML('beforeend', groupMoveButton());
      }
      modal.insertAdjacentHTML('beforeend', desktopMenuArea());
    }
  }
  setupMenuArea();

  // —— DOM 完成後に Swiper 初期化 ——
  if (window.storeeelItems.isMobile) {
    // SP＋circle の場合
    if (window.storeeelItems.frame === 'circle') {
      const idx = window.storeeelItems.contentGroups.findIndex(
        ({ id }) => id == window.iconId
      );
      if (idx !== -1) {
        initSwiper({ initialSlide: idx, element: modal.querySelector('.storeeelSwiper') });
      }
    } else {
      // SP＋square の場合: クリックしたグループのスライドを初期表示
      const resumeIndex = Number.isFinite(questionnaireResumePosition?.realIndex)
        ? questionnaireResumePosition.realIndex
        : null;
      const idx = window.storeeelItems.contentGroups.findIndex(
        ({ id }) => id == window.iconId
      );
      const initialSlide = idx !== -1 ? idx : (resumeIndex ?? 0);
      // window.iconId は上部（:91付近）でプレフィックス除去済みのグループIDなので、
      // ここで再分割するとフィードフォルダの仮想グループIDが壊れる
      window._storeeelSquarePendingQuestionnaireGroupId = String(window.iconId || '');
      window._storeeelSquarePendingQuestionnaireIndex = initialSlide;
      initSwiper({ initialSlide, element: modal.querySelector('.storeeelSwiper') });
    }
  } else {
    // PC
    const baseSlide = window.moviePosition || 0;
    // リール型・タイル型ではloop複製スライドのoffsetを反映する
    const loopOffset = isReelLike(window.storeeelItems.frame)
      ? (window._storeeelPcSquareLoopOffset || 0)
      : 0;
    initSwiper({ initialSlide: baseSlide + loopOffset, element: modal.querySelector('.storeeelSwiper') });
  }
  const swiper = getSwiperInstance();
  if (window.storeeelItems.isMobile) {
    window.__storeeelQuestionnaireResumePosition = null;
  }
  // PC + square(loop) は Swiper 側で active が調整されることがあるため、初期表示後に realIndex と同期する
  // PC + リール型・タイル型(loop) は Swiper 側で active が調整されることがあるため、初期表示後に realIndex と同期する
  if (!window.storeeelItems.isMobile && isReelLike(window.storeeelItems.frame) && swiper?.params?.loop) {
    const idx = Number.isFinite(swiper.realIndex) ? swiper.realIndex : 0;
    window.moviePosition = normalizeSquareContentIndex(idx);
  }
  let startInitialProgress = null;
  // SP初期表示時の進捗バー開始（正しいindexで、アクティブスライド内だけを更新）
  if (window.storeeelItems.isMobile) {
    const revealInitialCtaInWrapper = (wrapper) => {
      const host = wrapper?.querySelector?.('[data-storeeel-cta-host]');
      const frame = host?.shadowRoot?.querySelector('.ctaFrame');
      const text = host?.shadowRoot?.querySelector('.text-wrapper')?.textContent?.trim() || '';
      if (!frame || !text) return;
      frame.style.visibility = 'visible';
    };

    startInitialProgress = () => {
      const activeSlide = document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active');
      if (!activeSlide) return;
      const wrapper = activeSlide.querySelector('.slide-media-wrapper');
      showMediaLoadingShimmer(wrapper);
      // 初期表示もCTA更新経路を通し、メディア寸法確定後にだけ表示する
      updateCtaButtonAction(activeSlide);

      // リール型・タイル型はグループ内インデックスを0固定、ストーリー型はmoviePositionを使う
      const idx = isReelLike(window.storeeelItems.frame) ? 0 : Number(window.moviePosition || 0);

      const video = activeSlide.querySelector('video.storeeel-movie');
      const image = activeSlide.querySelector('img.storeeel-media-image');

      if (video) {
        if (video._onLoadedMetadataForProgress) {
          video.removeEventListener('loadedmetadata', video._onLoadedMetadataForProgress);
          video._onLoadedMetadataForProgress = null;
        }
        if (video._onPlayingForProgress) {
          video.removeEventListener('playing', video._onPlayingForProgress);
          video._onPlayingForProgress = null;
        }

        const isStillActive = () => {
          const slide = document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active');
          return !!slide && slide === activeSlide && slide.contains(video);
        };

        const cleanup = () => {
          if (video._onLoadedMetadataForProgress) {
            video.removeEventListener('loadedmetadata', video._onLoadedMetadataForProgress);
            video._onLoadedMetadataForProgress = null;
          }
          if (video._onPlayingForProgress) {
            video.removeEventListener('playing', video._onPlayingForProgress);
            video._onPlayingForProgress = null;
          }
        };

        const syncInitialCta = () => {
          if (!isStillActive()) return false;
          updateCtaHostPositionInWrapper(wrapper, { mediaEl: video });
          revealInitialCtaInWrapper(wrapper);
          return true;
        };

        const startProgress = () => {
          if (!isStillActive()) {
            cleanup();
            return;
          }
          const duration = Number(video.duration);
          if (!Number.isFinite(duration) || duration <= 0) return;
          // 初期表示時もメディアの実サイズ確定後にCTA位置を再計算し、タップ切替時と高さを揃える
          updateCtaHostPositionInWrapper(wrapper, { mediaEl: video });
          animateCurrentProgressBarInElement(duration, idx, activeSlide);
          hideMediaLoadingShimmer(wrapper);
          cleanup();
        };

        const tryStartProgress = () => {
          if (!isStillActive()) {
            cleanup();
            return false;
          }
          if (video.paused || video.ended) return false;
          if (video.readyState < 2) return false;
          startProgress();
          return true;
        };

        // 再生開始まで進捗を開始しない
        prepareCurrentProgressBarInElement(idx, activeSlide);
        video.addEventListener('loadedmetadata', syncInitialCta);
        video._onLoadedMetadataForProgress = () => { if (tryStartProgress()) cleanup(); };
        video._onPlayingForProgress = () => { if (tryStartProgress()) cleanup(); };
        video.addEventListener('loadedmetadata', video._onLoadedMetadataForProgress);
        video.addEventListener('playing', video._onPlayingForProgress);
        if (video.readyState >= 1) syncInitialCta();
        tryStartProgress();
      } else if (image) {
        if (image._onLoadForProgress) {
          image.removeEventListener('load', image._onLoadForProgress);
          image._onLoadForProgress = null;
        }
        if (image._onErrorForProgress) {
          image.removeEventListener('error', image._onErrorForProgress);
          image._onErrorForProgress = null;
        }

        const isStillActive = () => {
          const slide = document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active');
          if (!slide || slide !== activeSlide || !slide.contains(image)) return false;
          const cs = window.getComputedStyle(image);
          if (cs.display === 'none' || cs.visibility === 'hidden' || cs.opacity === '0') return false;
          return true;
        };

        const cleanup = () => {
          if (image._onLoadForProgress) {
            image.removeEventListener('load', image._onLoadForProgress);
            image._onLoadForProgress = null;
          }
          if (image._onErrorForProgress) {
            image.removeEventListener('error', image._onErrorForProgress);
            image._onErrorForProgress = null;
          }
        };

        const syncInitialCta = () => {
          if (!isStillActive()) return false;
          updateCtaHostPositionInWrapper(wrapper, { mediaEl: image });
          revealInitialCtaInWrapper(wrapper);
          return true;
        };

        const startProgress = () => {
          if (!isStillActive()) {
            cleanup();
            return;
          }
          // 初期表示時もメディアの実サイズ確定後にCTA位置を再計算し、タップ切替時と高さを揃える
          updateCtaHostPositionInWrapper(wrapper, { mediaEl: image });
          animateCurrentProgressBarInElement(5, idx, activeSlide);
          hideMediaLoadingShimmer(wrapper);
          cleanup();
        };

        const tryStartProgress = () => {
          if (!isStillActive()) {
            cleanup();
            return false;
          }
          if (!isMediaImageReady(image)) return false;
          startProgress();
          return true;
        };

        // 読み込み完了後に進捗を開始する
        prepareCurrentProgressBarInElement(idx, activeSlide);
        image.addEventListener('load', syncInitialCta);
        image._onLoadForProgress = () => { tryStartProgress(); };
        image._onErrorForProgress = () => { cleanup(); };
        image.addEventListener('load', image._onLoadForProgress);
        image.addEventListener('error', image._onErrorForProgress);
        if (isMediaImageReady(image)) syncInitialCta();
        tryStartProgress();
      }
    };

    startInitialProgress();

    if (window.storeeelItems.isMobile && window.storeeelItems.frame === 'circle') {
      const activeSlide = document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active');
      const wrapper = activeSlide?.querySelector?.('.slide-media-wrapper');
      if (wrapper) {
        preloadNextStoryVideo(wrapper, contents);
      }
    }
  }

  const restartShareProgressForActiveSlide = (targetContentId = null, remainingAttempts = 10) => {
    const activeSlide = document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active');
    if (!activeSlide) return;
    const wrapper = activeSlide.querySelector('.slide-media-wrapper');
    // リール型・タイル型はグループ内インデックスを0固定
    const idx = isReelLike(window.storeeelItems.frame) ? 0 : Number(window.moviePosition || 0);
    if (!Number.isFinite(idx) || idx < 0) return;

    const videoSelector = targetContentId
      ? `video.storeeel-movie[content-id="${targetContentId}"]`
      : 'video.storeeel-movie';
    const imageSelector = targetContentId
      ? `img.storeeel-media-image[content-id="${targetContentId}"]`
      : 'img.storeeel-media-image';
    const video = activeSlide.querySelector(videoSelector);
    const image = activeSlide.querySelector(imageSelector);

    // 共有遷移前のバー状態（例: 画像5秒）を引き継がないように初期化する
    prepareCurrentProgressBarInElement(idx, activeSlide);

    if (video) {
      if (video._onLoadedMetadataForProgress) {
        video.removeEventListener('loadedmetadata', video._onLoadedMetadataForProgress);
        video._onLoadedMetadataForProgress = null;
      }
      if (video._onPlayingForProgress) {
        video.removeEventListener('playing', video._onPlayingForProgress);
        video._onPlayingForProgress = null;
      }

      const cleanup = () => {
        if (video._onLoadedMetadataForProgress) {
          video.removeEventListener('loadedmetadata', video._onLoadedMetadataForProgress);
          video._onLoadedMetadataForProgress = null;
        }
        if (video._onPlayingForProgress) {
          video.removeEventListener('playing', video._onPlayingForProgress);
          video._onPlayingForProgress = null;
        }
      };

      const startFromDuration = () => {
        const duration = Number(video.duration);
        if (!Number.isFinite(duration) || duration <= 0) return false;
        animateCurrentProgressBarInElement(duration, idx, activeSlide);
        hideMediaLoadingShimmer(wrapper);
        return true;
      };

      video._onLoadedMetadataForProgress = () => {
        if (startFromDuration()) cleanup();
      };
      video._onPlayingForProgress = () => {
        if (startFromDuration()) cleanup();
      };
      video.addEventListener('loadedmetadata', video._onLoadedMetadataForProgress);
      video.addEventListener('playing', video._onPlayingForProgress);

      if (startFromDuration()) cleanup();
      return;
    }

    if (image) {
      const startImageProgress = () => {
        animateCurrentProgressBarInElement(5, idx, activeSlide);
        hideMediaLoadingShimmer(wrapper);
      };
      if (isMediaImageReady(image)) {
        startImageProgress();
      } else {
        image.addEventListener('load', startImageProgress, { once: true });
      }
      return;
    }

    if (remainingAttempts > 1) {
      window.setTimeout(() => {
        restartShareProgressForActiveSlide(targetContentId, remainingAttempts - 1);
      }, 60);
      return;
    }
    hideMediaLoadingShimmer(wrapper);
  };

  const applyShareOpen = () => {
    const shareState = window.__storeeelShareOpen;
    if (!shareState || shareState.applied) return;
    shareState.applied = true;

    // DOMの content-id 属性と照合するため、実際に描画されるcontentのidを使う。
    // 連携配信は共有URLのcontentId（展開元）と描画されるid（仮想ID）が別物になるため、
    // params.contentId で探すと見つからず、表示待ちがタイムアウトするまで待たされる。
    const contentId = shareState?.targetContentId ?? shareState?.params?.contentId ?? null;
    const restoreModal = shareState?.restoreModal;
    const clearShareTimeout = () => {
      if (shareState?.timeoutId != null) {
        clearTimeout(shareState.timeoutId);
        shareState.timeoutId = null;
      }
    };

    if (
      window.storeeelItems.isMobile
      && window.storeeelItems.frame === 'circle'
      && contentId
      && Array.isArray(contents)
      && window.moviePosition > 0
    ) {
      // 共有リンクで対象コンテンツへ切り替えた直後は commonChangeMediaProcess 側で view を送るため、
      // playEventHandler の初回送信を1回だけ抑止して二重送信を防ぐ。
      window.__storeeelSkipInitialPlayMediaViewTracking = true;
      commonChangeMediaProcess(contents);
      // 共有リンクで2件目以降を開いた場合、切替後メディアに進捗バーを同期する
      restartShareProgressForActiveSlide(contentId);
    }

    const revealIfReady = () => {
      if (typeof restoreModal !== 'function') return true;
      const activeSlide = document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active');
      if (!activeSlide) return false;
      if (contentId) {
        const media = activeSlide.querySelector(`[content-id="${contentId}"]`);
        if (!media) return false;
        const cs = window.getComputedStyle(media);
        if (cs.display === 'none' || cs.visibility === 'hidden' || cs.opacity === '0') {
          const thumb = activeSlide.querySelector('.storeeel-video-thumbnail');
          if (!thumb) return false;
          const tcs = window.getComputedStyle(thumb);
          if (tcs.display === 'none' || tcs.visibility === 'hidden' || tcs.opacity === '0') return false;
        }
      }
      clearShareTimeout();
      restoreModal();
      delete window.__storeeelShareOpen;
      return true;
    };

    if (revealIfReady()) return;

    let tries = 0;
    const maxTries = 25;
    const scheduleReveal = () => {
      tries += 1;
      if (revealIfReady()) return;
      if (tries >= maxTries) {
        clearShareTimeout();
        restoreModal?.();
        delete window.__storeeelShareOpen;
        return;
      }
      setTimeout(scheduleReveal, 100);
    };
    setTimeout(scheduleReveal, 100);
    return;
  };
  applyQuestionnaireResumeOpen({
    questionnaireResumePosition,
    contents,
    restartProgressForActiveSlide: restartShareProgressForActiveSlide,
  });
  applyShareOpen();

  // 2025-05-03: 初期のスライド状態を外部で適用
  applySlideScaling();
  updateHeaderWidthToSlide();
  updateAllCtaHostPositions();

  swiper.on('slideChangeTransitionStart', () => {
    applySlideScaling();
    updateHeaderWidthToSlide();
    updateAllCtaHostPositions();
  });

  // PC + リール型・タイル型 の loop 環境では、複製スライド等の影響で
  // window.moviePosition / 進捗バー / GA4 の参照先がズレることがあるため、
  // 「中央（active）のスライド確定後」にDOMを基準として同期する。
  if (!window.storeeelItems.isMobile && isReelLike(window.storeeelItems.frame)) {
    let lastTrackedMediaId = null;
    let squareSyncSeq = 0;
    let squareTrackFallbackTimerId = null;

    const resolveActiveSlide = () =>
      document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active');

    const resolveActiveRealIndex = (activeSlide) => {
      if (!activeSlide) return null;
      const raw = activeSlide.getAttribute?.('data-swiper-slide-index');
      const idx = raw == null ? NaN : Number(raw);
      if (Number.isFinite(idx)) return normalizeSquareContentIndex(idx);

      const fallback = swiper?.params?.loop ? swiper.realIndex : swiper?.activeIndex;
      return Number.isFinite(fallback) ? normalizeSquareContentIndex(fallback) : null;
    };

    const syncMoviePosition = (activeSlide) => {
      const idx = resolveActiveRealIndex(activeSlide);
      if (idx == null) return;
      window.moviePosition = idx;
    };

    const startProgressFromActiveSlide = (activeSlide, syncSeq) => {
      if (!activeSlide) return;
      const wrapper = activeSlide.querySelector('.slide-media-wrapper');
      showMediaLoadingShimmer(wrapper);

      const mediaEl = activeSlide.querySelector('video.storeeel-movie, img.storeeel-media-image');
      const startProgress = (duration) => {
        if (syncSeq !== squareSyncSeq) return;
        const safe = Number.isFinite(duration) && duration > 0 ? duration : 5;
        animateCurrentProgressBar(safe, 0);
        hideMediaLoadingShimmer(wrapper);
      };

      if (mediaEl && mediaEl.tagName === 'VIDEO') {
        const video = mediaEl;
        if (video._onLoadedMetadataForProgress) {
          video.removeEventListener('loadedmetadata', video._onLoadedMetadataForProgress);
          video._onLoadedMetadataForProgress = null;
        }
        if (video._onPlayingForProgress) {
          video.removeEventListener('playing', video._onPlayingForProgress);
          video._onPlayingForProgress = null;
        }

        const cleanup = () => {
          if (video._onLoadedMetadataForProgress) {
            video.removeEventListener('loadedmetadata', video._onLoadedMetadataForProgress);
            video._onLoadedMetadataForProgress = null;
          }
          if (video._onPlayingForProgress) {
            video.removeEventListener('playing', video._onPlayingForProgress);
            video._onPlayingForProgress = null;
          }
        };

        const tryStartProgress = () => {
          if (syncSeq !== squareSyncSeq) {
            cleanup();
            return false;
          }
          if (video.paused || video.ended) return false;
          if (video.readyState < 2) return false;
          startProgress(video.duration);
          cleanup();
          return true;
        };

        // 再生開始まで進捗を開始しない
        prepareCurrentProgressBar(0);
        video._onLoadedMetadataForProgress = () => { if (tryStartProgress()) cleanup(); };
        video._onPlayingForProgress = () => { if (tryStartProgress()) cleanup(); };
        video.addEventListener('loadedmetadata', video._onLoadedMetadataForProgress);
        video.addEventListener('playing', video._onPlayingForProgress);
        tryStartProgress();
      } else if (mediaEl) {
        const image = mediaEl;
        if (image._onLoadForProgress) {
          image.removeEventListener('load', image._onLoadForProgress);
          image._onLoadForProgress = null;
        }
        if (image._onErrorForProgress) {
          image.removeEventListener('error', image._onErrorForProgress);
          image._onErrorForProgress = null;
        }

        const cleanup = () => {
          if (image._onLoadForProgress) {
            image.removeEventListener('load', image._onLoadForProgress);
            image._onLoadForProgress = null;
          }
          if (image._onErrorForProgress) {
            image.removeEventListener('error', image._onErrorForProgress);
            image._onErrorForProgress = null;
          }
        };

        const tryStartProgress = () => {
          if (syncSeq !== squareSyncSeq) {
            cleanup();
            return false;
          }
          if (!isMediaImageReady(image)) return false;
          startProgress(5);
          cleanup();
          return true;
        };

        // 読み込み完了後に進捗を開始する
        prepareCurrentProgressBar(0);
        image._onLoadForProgress = () => { tryStartProgress(); };
        image._onErrorForProgress = () => { cleanup(); };
        image.addEventListener('load', image._onLoadForProgress);
        image.addEventListener('error', image._onErrorForProgress);
        tryStartProgress();
      } else {
        startProgress(5);
      }
    };

    const trackFromActiveSlide = (activeSlide) => {
      if (!activeSlide) return;
      const mediaEl = activeSlide.querySelector('video.storeeel-movie, img.storeeel-media-image');
      const mediaId = mediaEl?.getAttribute?.('media-id') || '';
      const mediaName = mediaEl?.getAttribute?.('media-name') || '';
      const mediaAssetType = activeSlide.querySelector('.slide-media-wrapper')?.dataset?.assetType;

      // 計測とローカルストレージ保存（中央に表示されているメディア基準）。
      // media_id が空のイベントは集計に乗らないため送らない（isTrackableMediaId のコメント参照）
      if (isTrackableMediaId(mediaId) && mediaId !== lastTrackedMediaId) {
        const freqWrapper = activeSlide.querySelector('.slide-media-wrapper');
        const isFreqInsert = freqWrapper?.dataset?.isFrequencyInsertContent === 'true';
        if (isFreqInsert) {
          const freqContentId = freqWrapper?.dataset?.frequencyInsertContentId || mediaId;
          trackStoreeelEvent('storeeel_frequency_insert_media_view', {
            storeeel_name: mediaName,
            ...buildMediaAssetIdParam(freqContentId, mediaAssetType),
            campaign_id: window.storeeelItems.id,
            // PC リール型はグループ概念がないため group_id は送らない
          });
        } else {
          trackStoreeelEvent('storeeel_media_asset_view', buildMediaAssetViewParams({
            storeeelName: mediaName,
            groupId: null,
            mediaId,
            campaignId: window.storeeelItems.id,
            mediaAssetType,
          }));
        }
        localStorageRepositories.get('mediaView').set(mediaId, window.storeeelItems.id, null, mediaAssetType);
        lastTrackedMediaId = mediaId;
      }
    };

    const clearSquareTrackFallback = () => {
      if (squareTrackFallbackTimerId == null) return;
      clearTimeout(squareTrackFallbackTimerId);
      squareTrackFallbackTimerId = null;
    };

    const scheduleSquareTrackFallback = () => {
      clearSquareTrackFallback();
      // loop 環境で transitionEnd が欠落するケースを補完する
      const fallbackDelay = Math.max(600, Number(swiper?.params?.speed || 0) + 250);
      squareTrackFallbackTimerId = window.setTimeout(() => {
        squareTrackFallbackTimerId = null;
        const activeSlide = resolveActiveSlide();
        if (!activeSlide) return;
        syncMoviePosition(activeSlide);
        trackFromActiveSlide(activeSlide);
      }, fallbackDelay);
    };

    swiper.on('slideChangeTransitionStart', () => {
      clearImageTimer();
      const syncSeq = ++squareSyncSeq;
      const activeSlide = resolveActiveSlide();
      if (!activeSlide) return;

      syncMoviePosition(activeSlide);
      startProgressFromActiveSlide(activeSlide, syncSeq);

      // 再生・音量表示・次へ自動再生は active に合わせる
      activeVideoPlay();
      checkVolumeButtonState();
      setupAutoPlayNextAction();
      scheduleSquareTrackFallback();
    });
    swiper.on('slideChangeTransitionEnd', () => {
      clearSquareTrackFallback();
      const activeSlide = resolveActiveSlide();
      if (!activeSlide) return;
      syncMoviePosition(activeSlide);
      trackFromActiveSlide(activeSlide);
    });
  }

  if (!window.storeeelItems.isMobile) {
    // PC + circle のみ、グループ内のスライド全てにCTAボタンを追加
    // （PC + square は createSlide 側で付与済み）
    if (window.storeeelItems.frame === 'circle') {
      const allWrappers = document.querySelectorAll('.storeeel-swiper-group-wrapper .swiper-slide .slide-media-wrapper');
      allWrappers.forEach((wrapper, index) => {
        const ctaHost = ctaButton(contents[index]);
        if (ctaHost) {
          wrapper.style.cursor = 'pointer';
          wrapper.appendChild(ctaHost);
          updateCtaHostPositionInWrapper(wrapper);
        }
      });
    }
  }

  // 端末回転や画面サイズ変更時にもCTA位置を再計算する
  if (!window._storeeelCtaViewportListenerBound) {
    window._storeeelCtaViewportListenerBound = true;
    window._storeeelCtaViewportListener = () => {
      if (!document.querySelector('.storeeel-modal')) return;
      window.requestAnimationFrame(() => {
        updateAllCtaHostPositions();
      });
    };
    window.addEventListener('resize', window._storeeelCtaViewportListener);
    window.addEventListener('orientationchange', window._storeeelCtaViewportListener);
  }

  // 各種共通アクション
  // activeのスライドの動画のみ再生
  // SP + リール型・タイル型 は groupSwipeAction 側で「スライド確定後」に再生させるため、ここでは再生しない
  if (!(window.storeeelItems.isMobile && isReelLike(window.storeeelItems.frame))) {
    activeVideoPlay();
  }
  // 閉じるボタンをクリックしたときの処理
  closeButtonClickAction();
  // 音量ボタンをクリックしたときの処理
  volumeButtonClickAction();
  // シェアボタンをクリックしたときの処理
  sharingClickAction();
  // 停止/再開ボタンはPC/SP共通で同じDOMを操作する
  mobileVideoControls();
  // 全動画をプリロード(再生スピード向上のため)
  preloadVideo();
  // 動画を開いた時に一番初めに実行される関数
  playEventHandler();
  // 動画の進捗をトラッキング(レポート用)
  trackMediaProcess();
  // CTAボタンをクリックしたときの処理
  clickCtaAction();
  // 別タブ移動時の処理(まだうまくいっていない)
  visibilityChangeAction();
  // 動画が停止・バッファ中の間は進捗バーも停止する。
  videoBufferingAction();
  if (window.storeeelItems.isMobile) {
    // スマホ専用アクション
    // 動画終了後の次の動画を自動再生する
    mobileAutoPlayNextAction();
    // スライドした時のグループ移動処理
    groupSwipeAction();
    // スライドで閉じるアクション
    closeSwipeAction();
    if (window.storeeelItems.frame === "circle") {
      // SP + circle のみタップでの移動を有効化
      changeMovieTapAction();
      // スライドのCTAボタンをクリックしたときの処理
      swipeCtaAction();
      // 初回限定アニメーション（SP + circle）
      circleOverlayAction();
      // 初期表示時も、タップ遷移と同じCTA更新経路を通して位置を一致させる
      const syncInitialCircleCta = () => {
        const activeSlide = document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active');
        if (!activeSlide) return;
        updateCtaButtonAction(activeSlide);
        const activeWrapper = activeSlide.querySelector('.slide-media-wrapper');
        if (!activeWrapper) return;
        const activeMedia = activeSlide.querySelector('video.storeeel-movie.active, img.storeeel-media-image.active')
          || activeSlide.querySelector('video.storeeel-movie, img.storeeel-media-image');
        if (!activeMedia) return;

        // CTAテキスト有無に依存せず、CTAホスト位置を直接再計算してタップ時と揃える
        updateCtaHostPositionInWrapper(activeWrapper, { mediaEl: activeMedia });

        if (activeMedia.tagName === 'VIDEO') {
          const recalc = () => {
            const currentActive = document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active');
            if (currentActive !== activeSlide) return;
            updateCtaHostPositionInWrapper(activeWrapper, { mediaEl: activeMedia });
          };
          activeMedia.addEventListener('loadedmetadata', recalc, { once: true });
          activeMedia.addEventListener('loadeddata', recalc, { once: true });
        } else if (activeMedia.tagName === 'IMG' && !activeMedia.complete) {
          const recalc = () => {
            const currentActive = document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active');
            if (currentActive !== activeSlide) return;
            updateCtaHostPositionInWrapper(activeWrapper, { mediaEl: activeMedia });
          };
          activeMedia.addEventListener('load', recalc, { once: true });
        }
      };
      window.requestAnimationFrame(syncInitialCircleCta);
      window.setTimeout(syncInitialCircleCta, 0);
      window.setTimeout(syncInitialCircleCta, 120);
    }else {
      // 初回限定アニメーション（SP + square）
      introSquareSlideAnimation();
    }
  } else {
    // PC専用アクション
    desktopArrowIconsAction();
    // 動画の終了後の次の動画を自動再生する
    setupAutoPlayNextAction();
    // PC 時は常にスライドクリックで該当スライドに移動
    slideClickNavigateAction();
    if (window.storeeelItems.frame === "circle") {
      // circle モードのみグループ移動ボタンを有効化
      groupMoveButtonAction();
    }
  }

  // SP + リール型・タイル型 は初期スライド確定後に groupSwipeAction 側でアンケート判定する
  if (!(window.storeeelItems.isMobile && isReelLike(window.storeeelItems.frame))) {
    // 初回表示コンテンツが広告なら、モーダル表示後にだけ mock を重ねる
    window.requestAnimationFrame(() => {
      maybeShowTargetingQuestionnaireForCurrentContent();
    });
  }
};
